import React, { useState, useMemo } from 'react';
import { useStore } from '../context/StoreContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { format, parseISO, isAfter, isBefore, startOfDay, addDays } from 'date-fns';
import VisitModal from '../components/VisitModal';

const { FiCalendar, FiPlus, FiClock, FiUser, FiMapPin, FiSearch, FiFilter, FiTrash2, FiEdit2, FiCheckCircle, FiZap, FiArrowRight, FiActivity, FiArchive } = FiIcons;
const FiHistory = FiArchive;

const PropertyVisits = () => {
  const { data, addRecord, updateRecord, deleteRecord, logAccess } = useStore();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingVisit, setEditingVisit] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('upcoming'); // 'upcoming', 'past'

  const visits = data.propertyVisits || [];
  const today = startOfDay(new Date());

  const filteredVisits = useMemo(() => {
    return visits
      .filter(v => {
        const prop = data.properties.find(p => p.id === v.propertyId);
        const matchesSearch = 
          v.visitorName.toLowerCase().includes(searchTerm.toLowerCase()) || 
          prop?.name?.toLowerCase().includes(searchTerm.toLowerCase());
        
        const visitDate = parseISO(v.visitDate);
        const isUpcoming = isAfter(visitDate, today) || format(visitDate, 'yyyy-MM-dd') === format(today, 'yyyy-MM-dd');
        
        if (activeTab === 'upcoming') return matchesSearch && isUpcoming;
        return matchesSearch && !isUpcoming;
      })
      .sort((a, b) => new Date(a.visitDate) - new Date(b.visitDate));
  }, [visits, searchTerm, activeTab, today, data.properties]);

  const stats = {
    totalUpcoming: visits.filter(v => isAfter(parseISO(v.visitDate), today)).length,
    showingsThisWeek: visits.filter(v => {
      const date = parseISO(v.visitDate);
      return isAfter(date, today) && isBefore(date, addDays(today, 7));
    }).length,
    completedVisits: visits.filter(v => v.status === 'Completed').length
  };

  const handleSaveVisit = (visit) => {
    if (editingVisit) {
      updateRecord('propertyVisits', editingVisit.id, visit);
      logAccess(`Updated visit for ${visit.visitorName}`, 'Admin', 'Success');
    } else {
      addRecord('propertyVisits', visit);
      
      // Auto-log communication if notification is enabled
      if (visit.notifyTenant) {
        const prop = data.properties.find(p => p.id === visit.propertyId);
        if (prop && prop.tenantId) {
          addRecord('communications', {
            tenantId: prop.tenantId,
            propertyId: prop.id,
            type: 'Email',
            date: format(new Date(), 'yyyy-MM-dd'),
            subject: `Notice of Property Visit - ${prop.name}`,
            notes: `Automated notice sent for ${visit.type} on ${visit.visitDate} at ${visit.startTime}.`,
            status: 'Sent'
          });
        }
      }
      logAccess(`Scheduled new ${visit.type} for ${visit.visitorName}`, 'Admin', 'Success');
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500 pb-12">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-black text-gray-800 tracking-tight">Visit Orchestrator</h2>
          <p className="text-sm text-gray-500 font-medium italic">Managing property access for showings, inspections, and appraisals.</p>
        </div>
        <button 
          onClick={() => { setEditingVisit(null); setIsModalOpen(true); }}
          className="flex items-center px-6 py-3 bg-indigo-600 text-white rounded-2xl hover:bg-indigo-700 transition shadow-lg shadow-indigo-100 font-black text-sm uppercase tracking-wider"
        >
          <SafeIcon icon={FiPlus} className="mr-2" /> Schedule Visit
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden group">
          <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Upcoming Appointments</p>
          <p className="text-3xl font-black text-gray-900">{stats.totalUpcoming}</p>
          <SafeIcon icon={FiCalendar} className="absolute -bottom-2 -right-2 text-indigo-50 opacity-0 group-hover:opacity-100 transition-opacity" size={60} />
        </div>
        <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm">
          <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Next 7 Days</p>
          <p className="text-3xl font-black text-indigo-600">{stats.showingsThisWeek} <span className="text-xs font-bold text-gray-400">Visits</span></p>
        </div>
        <div className="bg-indigo-600 p-6 rounded-3xl text-white shadow-xl relative overflow-hidden group">
          <h3 className="text-[10px] font-black uppercase tracking-widest opacity-80 mb-2">Portfolio Activity</h3>
          <p className="text-3xl font-black">{stats.completedVisits} Completed</p>
          <p className="text-[10px] font-bold uppercase mt-1 text-indigo-100">Historical access logs</p>
          <SafeIcon icon={FiActivity} className="absolute -bottom-4 -right-4 text-white/10 group-hover:scale-110 transition-transform" size={80} />
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-4 bg-white p-4 rounded-3xl shadow-sm border border-gray-100">
        <div className="flex bg-gray-50 p-1 rounded-2xl">
          <button 
            onClick={() => setActiveTab('upcoming')}
            className={`flex items-center px-6 py-2 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all ${activeTab === 'upcoming' ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-400'}`}
          >
            <SafeIcon icon={FiClock} className="mr-2" /> Upcoming
          </button>
          <button 
            onClick={() => setActiveTab('past')}
            className={`flex items-center px-6 py-2 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all ${activeTab === 'past' ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-400'}`}
          >
            <SafeIcon icon={FiHistory} className="mr-2" /> Archive
          </button>
        </div>
        <div className="relative flex-1">
          <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
          <input 
            type="text" 
            placeholder="Search visitor name or property..." 
            className="w-full pl-12 pr-4 py-2.5 bg-gray-50 border-none rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500 text-sm font-medium"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filteredVisits.map((visit) => {
          const prop = data.properties.find(p => p.id === visit.propertyId);
          const isToday = visit.visitDate === format(new Date(), 'yyyy-MM-dd');
          
          return (
            <div key={visit.id} className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 hover:shadow-xl transition-all group">
              <div className="flex justify-between items-start mb-6">
                <div className="flex items-center gap-4">
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg transition-transform group-hover:scale-105 ${isToday ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-500'}`}>
                    <div className="text-center">
                      <p className="text-[8px] font-black uppercase leading-none mb-1">{format(parseISO(visit.visitDate), 'MMM')}</p>
                      <p className="text-xl font-black leading-none">{format(parseISO(visit.visitDate), 'dd')}</p>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-black text-gray-900 leading-tight">{visit.visitorName}</h4>
                    <p className="text-[10px] text-indigo-600 font-bold uppercase tracking-widest mt-1">{visit.type}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button 
                    onClick={() => { setEditingVisit(visit); setIsModalOpen(true); }}
                    className="p-2 text-gray-400 hover:text-indigo-600 transition bg-gray-50 rounded-xl"
                  >
                    <SafeIcon icon={FiEdit2} size={16} />
                  </button>
                  <button 
                    onClick={() => deleteRecord('propertyVisits', visit.id)}
                    className="p-2 text-gray-400 hover:text-red-500 transition bg-gray-50 rounded-xl"
                  >
                    <SafeIcon icon={FiTrash2} size={16} />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100">
                  <p className="text-[9px] font-black text-gray-400 uppercase mb-2 flex items-center gap-1">
                    <SafeIcon icon={FiMapPin} size={10} /> Location
                  </p>
                  <p className="text-xs font-black text-gray-900 truncate">{prop?.name}</p>
                  <p className="text-[10px] text-gray-500 font-medium truncate">{prop?.address}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100">
                  <p className="text-[9px] font-black text-gray-400 uppercase mb-2 flex items-center gap-1">
                    <SafeIcon icon={FiClock} size={10} /> Time Window
                  </p>
                  <p className="text-xs font-black text-gray-900">{visit.startTime}</p>
                  <p className="text-[10px] text-gray-500 font-medium">{visit.duration} Minutes</p>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex gap-2">
                  {visit.notifyTenant && (
                    <span className="flex items-center gap-1.5 px-3 py-1 bg-green-50 text-green-600 rounded-lg text-[9px] font-black uppercase tracking-widest border border-green-100">
                      <SafeIcon icon={FiCheckCircle} size={10} /> Resident Notified
                    </span>
                  )}
                  {prop?.status === 'Occupied' && (
                    <span className="flex items-center gap-1.5 px-3 py-1 bg-amber-50 text-amber-600 rounded-lg text-[9px] font-black uppercase tracking-widest border border-amber-100">
                      <SafeIcon icon={FiUser} size={10} /> Occupied Unit
                    </span>
                  )}
                </div>
                <select 
                  className={`text-[9px] font-black uppercase tracking-widest rounded-lg px-3 py-1.5 border-none outline-none shadow-sm ${visit.status === 'Completed' ? 'bg-green-600 text-white' : 'bg-white text-indigo-600 border border-indigo-100'}`}
                  value={visit.status}
                  onChange={(e) => updateRecord('propertyVisits', visit.id, { status: e.target.value })}
                >
                  <option value="Scheduled">Scheduled</option>
                  <option value="Confirmed">Confirmed</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Completed">Completed</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
              </div>
            </div>
          );
        })}

        {filteredVisits.length === 0 && (
          <div className="col-span-full py-24 text-center bg-white rounded-3xl border border-dashed border-gray-200">
            <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiCalendar} size={32} className="text-gray-200" />
            </div>
            <h4 className="text-lg font-black text-gray-900">No Appointments Found</h4>
            <p className="text-xs font-black text-gray-400 uppercase tracking-widest mt-1">Try adjusting your search or filters</p>
          </div>
        )}
      </div>

      <VisitModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        onSave={handleSaveVisit} 
        properties={data.properties} 
        initialData={editingVisit} 
      />
    </div>
  );
};

export default PropertyVisits;